<template>
  <div
    class="icon-container"
  >
    <font-awesome-icon
      v-show="!isMuted && volumeLevelCategory === 2"
      :icon="['fas', 'volume-up']"
    />
    <font-awesome-icon
      v-show="!isMuted && volumeLevelCategory === 1"
      :icon="['fas', 'volume-down']"
    />
    <font-awesome-icon
      v-show="!isMuted && volumeLevelCategory === 0"
      :icon="['fas', 'volume-off']"
    />
    <font-awesome-icon
      v-show="isMuted"
      :icon="['fas', 'volume-mute']"
    />
    <div class="icon-hover-container-left">
      <transition name="fade">
        <div
          v-show="displayVolumeSlider"
          class="music-icon-hover-msg"
        >
          <div>
            Music +
          </div>
          <div>
            Sound Effects
          </div>
        </div>
      </transition>
    </div>
    <transition name="fade">
      <div
        v-show="displayVolumeSlider"
        class="volume-control-container"
        @mouseenter="$emit('mouse-entered-volume-slider')"
        @mouseleave="$emit('mouse-left-volume-slider')"
      >
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value="0.20"
          @input="setVolume"
        >
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from 'vue';

export default Vue.extend({
  name: 'MusicPlayer',
  props: {
    isMouseInsideVolumeButtonDelayed: Boolean,
    isMouseInsideVolumeSliderDelayed: Boolean,
    isMuted: Boolean
  },
  data: () => ({
    audioContext: null,
    gainNode: null,
    songUrlBase: 'https://weavr-audio.s3.amazonaws.com/songs',
    soundEffectUrlBase: 'https://weavr-audio.s3.amazonaws.com/sound-effects',
    songUrls: {
      'lobby': '/lobby.m4a',
      'prompts': '/prompts.m4a',
      'voting': '/voting.m4a',
      'ending': '/ending.m4a'
    },
    soundEffectUrls: {
      'answer-reveal': '/answer-reveal.m4a',
      'prompt-reveal': '/prompt-reveal.m4a',
      'vote-result-reveal': '/vote-result-reveal.m4a',
      'timer-0': '/timers/0.m4a',
      'timer-10': '/timers/10.m4a',
      'timer-30': '/timers/30.m4a',
      'timer-9-to-1': '/timers/9-to-1.m4a',
      'player-joined-lobby-1': '/player-joined-lobby/1.m4a',
      'player-joined-lobby-2': '/player-joined-lobby/2.m4a',
      'player-joined-lobby-3': '/player-joined-lobby/3.m4a',
      'player-joined-lobby-4': '/player-joined-lobby/4.m4a',
      'player-joined-lobby-5': '/player-joined-lobby/5.m4a',
      'player-joined-lobby-6': '/player-joined-lobby/6.m4a',
      'player-joined-lobby-7': '/player-joined-lobby/7.m4a',
      'player-joined-lobby-8': '/player-joined-lobby/8.m4a',
      'player-submitted-prompts-1': '/player-submitted-prompts/1.m4a',
      'player-submitted-prompts-2': '/player-submitted-prompts/2.m4a',
      'player-submitted-prompts-3': '/player-submitted-prompts/3.m4a',
      'player-submitted-prompts-4': '/player-submitted-prompts/4.m4a',
      'player-submitted-prompts-5': '/player-submitted-prompts/5.m4a',
      'player-submitted-prompts-6': '/player-submitted-prompts/6.m4a',
      'player-submitted-prompts-7': '/player-submitted-prompts/7.m4a',
      'player-submitted-prompts-8': '/player-submitted-prompts/8.m4a'
    },
    songBuffers: {},
    soundEffectBuffers: {},
    areSongBuffersReady: false,
    areSoundEffectBuffersReady: false,
    lowVolumeSoundEffectNames: [
      'answer-reveal'
    ],
    highVolumeSoundEffectNames: [
      'timer-10',
      'timer-0'
    ],
    currentBuffer: null,
    musicSource: null,
    allSoundEffectSources: [],
    allSoundEffectGainNodes: [],
    currentExponentialGain: (Math.exp(0.2) - 1) / (Math.exp(1) - 1),
    volumeLevelCategory: 1,
    canStartAudio: true
  }),
  computed: {
    displayVolumeSlider: function () {
      return this.isMouseInsideVolumeButtonDelayed || this.isMouseInsideVolumeSliderDelayed;
    }
  },
  watch: {
    isMuted: function(value) {
      if (value) {
        this.mute();
      } else {
        this.unmute();
      }
    }
  },
  methods: {
    timeout(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async prepareSongBuffers() {
      await Promise.all(Object.keys(this.songUrls).map(async (key) => {
        const response = await fetch(this.songUrlBase + this.songUrls[key]);
        const arrayBuffer = await response.arrayBuffer();
        this.songBuffers[key] = await new Promise((resolve, reject) => {
          this.audioContext.decodeAudioData(arrayBuffer, buffer => {
            resolve(buffer);
          }, error => {
            reject(error);
          });
        });
      }));

      this.areSongBuffersReady = true;
    },
    async prepareSoundEffectBuffers() {
      await Promise.all(Object.keys(this.soundEffectUrls).map(async (key) => {
        const response = await fetch(this.soundEffectUrlBase + this.soundEffectUrls[key]);
        const arrayBuffer = await response.arrayBuffer();
        this.soundEffectBuffers[key] = await new Promise((resolve, reject) => {
          this.audioContext.decodeAudioData(arrayBuffer, buffer => {
            resolve(buffer);
          }, error => {
            reject(error);
          });
        });
      }));

      this.areSoundEffectBuffersReady = true;
    },
    async playSong(songName) {
      await this.stopSong();
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      if (!this.areSongBuffersReady) {
        await this.prepareSongBuffers();
      }

      this.musicSource = this.audioContext.createBufferSource();
      this.musicSource.buffer = this.songBuffers[songName];
      this.musicSource.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);
      this.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      this.musicSource.loop = true;

      if (this.canStartAudio) {
        this.musicSource.start(0);
        if (!this.isMuted) {
          this.gainNode.gain.setTargetAtTime(this.currentExponentialGain, this.audioContext.currentTime, 4);
        }
      }
    },
    async stopSong() {
      if (this.musicSource) {
        if (!this.isMuted) {
          this.gainNode.gain.setTargetAtTime(0.01 * this.currentExponentialGain, this.audioContext.currentTime, 0.5);
        }
        await this.timeout(2000);
        this.musicSource.stop(0);
        this.musicSource.disconnect();
        this.musicSource = null;
      }
    },
    async stopSongImmediately() {
      if (this.musicSource) {
        if (!this.isMuted) {
          this.gainNode.gain.setTargetAtTime(0, this.audioContext.currentTime, 0.1);
        }
        await this.timeout(100);
        this.musicSource.stop(0);
        this.musicSource.disconnect();
        this.musicSource = null;
      }
    },
    async playSoundEffect(soundEffectName) {
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      if (!this.areSoundEffectBuffersReady) {
        await this.prepareSoundEffectBuffers();
      }

      if (!this.isMuted) {
        const soundEffectSource = this.audioContext.createBufferSource();
        const soundEffectGainNode = this.audioContext.createGain();
        this.allSoundEffectSources.push(soundEffectSource);
        this.allSoundEffectGainNodes.push(soundEffectGainNode);
        soundEffectSource.buffer = this.soundEffectBuffers[soundEffectName];
        soundEffectSource.connect(soundEffectGainNode);
        soundEffectGainNode.connect(this.audioContext.destination);
        const gainMultiplier = this.getSoundEffectGainMultiplier(soundEffectName);
        soundEffectGainNode.gain.setValueAtTime(gainMultiplier * this.currentExponentialGain, this.audioContext.currentTime);

        if (this.canStartAudio) {
          soundEffectSource.start(0);
          soundEffectSource.onended = () => {
            soundEffectSource.disconnect();
            soundEffectGainNode.disconnect();
          };
        }
      }
    },
    async stopAllSoundEffects() {
      if (!this.isMuted) {
        await Promise.all(this.allSoundEffectGainNodes.map(async (soundEffectGainNode) => {
          soundEffectGainNode.gain.setTargetAtTime(0, this.audioContext.currentTime, 0.1);
          await this.timeout(100);
        }));

        this.allSoundEffectGainNodes.forEach(soundEffectGainNode => {
          try {
            soundEffectGainNode.disconnect();
          } catch (error) {
            console.log('Error when disconnecting sound effect gain to zero:', error);
          }
        });
      }

      this.allSoundEffectSources.forEach(soundEffectSource => {
        try {
          soundEffectSource.stop(0);
          soundEffectSource.disconnect();
        } catch (error) {
          console.log('Error when stopping sound effect playback:', error);
        }
      });
    },
    async mute() {
      this.gainNode.gain.setTargetAtTime(0, this.audioContext.currentTime, 0.1);
      await this.timeout(100);
      this.gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
    },
    unmute() {
      this.gainNode.gain.setTargetAtTime(this.currentExponentialGain, this.audioContext.currentTime, 0.1);
    },
    setVolume(event) {
      this.currentExponentialGain = (Math.exp(event.target.value) - 1) / (Math.exp(1) - 1);

      if (this.currentExponentialGain === 0) {
        this.volumeLevelCategory = 0;
      } else if (this.currentExponentialGain > 0 && this.currentExponentialGain < 0.5) {
        this.volumeLevelCategory = 1;
      } else {
        this.volumeLevelCategory = 2;
      }

      if (!this.isMuted) {
        this.gainNode.gain.setTargetAtTime(this.currentExponentialGain, this.audioContext.currentTime, 0.1);
      }
    },
    getSoundEffectGainMultiplier(soundEffectName) {
      if (this.lowVolumeSoundEffectNames.includes(soundEffectName)) {
        return 1;
      } else if (this.highVolumeSoundEffectNames.includes(soundEffectName)) {
        return 2;
      }
      return 1.5;
    }
  },
  mounted: async function() {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioContext({ sampleRate: 48000 });
    this.gainNode = this.audioContext.createGain();
    await this.prepareSongBuffers();
    await this.prepareSoundEffectBuffers();
  },
  beforeDestroy: function() {
    this.canStartAudio = false;  // prevent async playSong and playSoundEffect functions from playing new audio
    this.stopAllSoundEffects();
    this.stopSongImmediately();
  }
})
</script>

<style scoped lang="scss">
.volume-control-container {
  display: flex;
  position: absolute;
  top: calc(50% - 1em - 4.6em);
  left: calc(50% - 3em);
  transform: rotate(-90deg);
  transform-origin: center;
  justify-content: center;
  background-color: $baby-blue;
  border-radius: 0.4em;
  height: 1.8em;
  width: 6em;
}

.icon-hover-container-left {
  position: absolute;
  top: -4.2em;
  left: -4.6em;
  pointer-events: none;
}

.music-icon-hover-msg {
  position: relative;
  left: -50%;
  padding: 0.3em 0.8em;
  color: $smoke-gray;
  border-radius: 0.4em;
  background-color: $baby-blue;
  white-space: nowrap;
}

input[type=range] {
  -webkit-appearance: none;
  width: 85%;
  background: transparent;
}

input[type=range]:focus {
  outline: none;
}

input[type=range]::-webkit-slider-runnable-track {
  width: 100%;
  height: 0.8em;
  border-radius: 0.4em;
  background-color: $light-smoke-gray;
  cursor: pointer;
}

input[type=range]::-moz-range-track {
  width: 100%;
  height: 0.8em;
  border-radius: 0.4em;
  background-color: $light-smoke-gray;
  cursor: pointer;
}

input[type=range]::-ms-track {
  width: 100%;
  height: 0.8em;
  border-radius: 0.4em;
  background-color: $light-smoke-gray;
  cursor: pointer;
}

input[type=range]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 1.8em;
  height: 1.8em;
  background: $natural-white;
  margin-top: -0.5em;
  border-radius: 50%;
  border: 0.2em solid $smoke-gray;
}

input[type=range]::-moz-range-thumb {
  width: 1.8em;
  height: 1.8em;
  background: $natural-white;
  border-radius: 50%;
  border: 0.2em solid $smoke-gray;
}

input[type=range]::-ms-thumb {
  width: 1.8em;
  height: 1.8em;
  background: $natural-white;
  border-radius: 50%;
  border: 0.2em solid $smoke-gray;
}
</style>
